import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { selectOnboardingBaseUrl } from '../store/selectors/onboarding-config.selectors';
import { OnboardingAuthService } from './onboarding-auth.service';
import { selectBffBaseUrl } from '../store/selectors/api-config.selectors';
import { ProcessType } from './two-factor-auth.service';

type ErrorResponseObject = {
  error: string;
  errorDescription: string;
};

export type EmailVerificationResponse = {
  validationErrors?: ErrorResponseObject[];
};

export type AccountCreationSuccessResponse = {
  id: string;
  emailAddress: string;
  status: 'CREATED' | 'ACCOUNT_CREATED';
};

export type AccountCreationResponse =
  | EmailVerificationResponse
  | AccountCreationSuccessResponse;

@Injectable({
  providedIn: 'root',
})
export class OnboardingService {
  private onboardingBaseUrl: string = '';
  private baseAppAccountPath: string = '';

  /** @todo  move methods with http requests to api services*/
  constructor(
    private http: HttpClient,
    private onboardingAuthService: OnboardingAuthService,
    private store: Store,
  ) {
    this.store
      .pipe(selectOnboardingBaseUrl)
      .subscribe((url) => (this.onboardingBaseUrl = url));
    this.store
      .pipe(selectBffBaseUrl)
      .subscribe(
        (url) => (this.baseAppAccountPath = url + '/app/account'),
      ); /** @todo move to app-config */
  }

  verifyEmail(email: string): Observable<EmailVerificationResponse> {
    return this.onboardingAuthService.getClientCredentials(false).pipe(
      switchMap(({ access_token, token_type }) => {
        return this.http.post<EmailVerificationResponse>(
          `${this.onboardingBaseUrl}/v2/registration/$validate/email`,
          email,
          {
            headers: {
              'Content-Type': 'text/plain',
              Authorization: `${token_type} ${access_token}`,
            },
            withCredentials: false,
          },
        );
      }),
    );
  }

  createAccount({
    emailAddress,
    locale,
  }: {
    emailAddress: string;
    locale: string;
  }): Observable<AccountCreationResponse> {
    return this.onboardingAuthService.getClientCredentials(false).pipe(
      switchMap(({ access_token, token_type }) => {
        return this.http.post<AccountCreationResponse>(
          `${this.onboardingBaseUrl}/v2/registration`,
          {
            emailAddress,
            locale,
            type: 'public-client',
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token_type} ${access_token}`,
            },
            withCredentials: false,
          },
        );
      }),
    );
  }

  cancelProcessInstance = (processId: string): Observable<any> => {
    return this.http.delete(
      `${this.baseAppAccountPath}/myaccount/process/instance/${processId}`,
    );
  };

  getActiveProcesses(type?: ProcessType): Observable<ProcessInstance[]> {
    return this.http
      .get<ProcessInstanceResponse>(
        `${this.baseAppAccountPath}/myaccount/process/instance?activeOnly=true` +
          (type ? `&type=${type}` : ''),
      )
      .pipe(map(({ processInstances }) => processInstances));
  }

  getPendingProcesses(type?: ProcessType): Observable<ProcessInstance[]> {
    return this.http
      .get<ProcessInstanceResponse>(
        `${this.baseAppAccountPath}/myaccount/process/instance?activeOnly=false` +
          (type ? `&type=${type}` : ''),
      )
      .pipe(map(({ processInstances }) => processInstances));
  }

  getOnboardingStatus(): Observable<any> {
    return this.http.get(`${this.baseAppAccountPath}/myaccount/onboarding`);
  }

  isOnboarded(): Observable<boolean> {
    return this.getOnboardingStatus().pipe(map(({ onboarded }) => !!onboarded));
  }

  hasPendingProcesses(): Observable<boolean> {
    return this.getPendingProcesses().pipe(
      map((processInstances = []) =>
        processInstances.some(
          (process) =>
            process.status !== 'completed' && process.status !== 'cancelled',
        ),
      ),
    );
  }

  getRecoveryProcessInstances(): Observable<ProcessInstance[]> {
    return this.getActiveProcesses('recovery');
  }

  isRecoveryMode(): Observable<boolean> {
    return this.getRecoveryProcessInstances().pipe(
      map((processInstances = []) => !!processInstances.length),
    );
  }

  cancelRecoveryInstances() {
    return this.getRecoveryProcessInstances().pipe(
      switchMap((processInstances = []) =>
        processInstances.length
          ? forkJoin(
              processInstances.map(({ processId }) =>
                this.cancelProcessInstance(processId),
              ),
            )
          : of(null),
      ),
    );
  }
}

type ProcessInstance = {
  processId: string;
  status:
    | 'pending'
    | 'suspended'
    | 'active'
    | 'hibernated'
    | 'interrupted'
    | 'completed'
    | 'cancelled';
};

type ProcessInstanceResponse = {
  processInstances: ProcessInstance[];
};
